// Copyright (c) Docugami, Inc. All rights reserved.

import { ContextualMenu, IButtonProps, Icon, IconButton, Modal, Point, TooltipDelay, TooltipHost } from "@fluentui/react";
import { GrabHandleContainer } from "components/cards/CardStyles";
import { ChunkTagInputField } from "components/cards/ChunkTagInputField";
import { primaryTheme, themeRed } from "components/shell/presentation/AppStyles";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useSdvEntityQuery } from "hooks/SdvEntityQuery";
import { useSdvResource } from "hooks/SdvResource";
import { useTagText } from "hooks/TagText";
import { MutableRefObject, useEffect, useState } from "react";
import { BoundsComponent } from "sdv/components/BoundsComponent";
import { TokenPositionComponent } from "sdv/components/TokenPositionComponent";
import { ViewComponent } from "sdv/components/ViewComponent";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { getPageForPos } from "sdv/Page";
import { worldToViewSpace } from "sdv/Space";
import { setSelectedLabel } from "store/reports/reportsSlice";
import { selectSelectedLabel } from "store/reports/selectors";
import { selectCurrentChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";

const InputContainer = styled.div`
    display: flex;
    padding: 0px 5px;
    box-sizing: border-box;
    align-items: center;
`;

const DragRoot = styled.div`
    display: flex;
    width: 260px;
    cursor: move;
`;

const Divider = styled.div`
    border-right: 1px solid grey;
    height: 32px;
`;

const GrabHandleRoot = styled(GrabHandleContainer)`
    padding: 0px 5px;
    margin-left: initial;
    :hover {
        cursor: move;
    }
`;

interface IEditorButtonProps extends IButtonProps {
    tooltipContent: string | JSX.Element | JSX.Element[] | undefined;
}

interface ChunkEditorProps {
    engine: Engine | null;
    documentId: string;
    clusterId: string;
    viewRef: MutableRefObject<HTMLElement | undefined>;
}

const selectedChunkComponents: ComponentId[] = [ComponentId.Selected, ComponentId.Bounds];
const highlightChunkComponents: ComponentId[] = [ComponentId.Selected, ComponentId.Highlighter, ComponentId.TokenPosition];
const viewComponents: ComponentId[] = [ComponentId.View];

const defaultModalPosition = { left: 500, top: 300 };
export const ChunkEditor = (props: ChunkEditorProps): JSX.Element => {
    const [position, setPosition] = useState<Point>(defaultModalPosition)

    const selectedLabel = useAppSelector(selectSelectedLabel);
    let instance = useAppSelector(selectCurrentChunkInstance);
    const isValidInstance = !!instance && !instance.addedChunkInfo;

    const tagText = useTagText(props.clusterId, props.documentId, instance);

    const dispatch = useAppDispatch();

    const { entities: selectedEntities } = useSdvEntityQuery(selectedChunkComponents, props.engine);
    const { entities: highlightEntities } = useSdvEntityQuery(highlightChunkComponents, props.engine);
    const { entities: viewEntities } = useSdvEntityQuery(viewComponents, props.engine);
    const pageTokens = useSdvResource("pageTokens", props.engine).value;
    const visiblePages = useSdvResource("visiblePages", props.engine).value;

    useEffect(() => {
        if (!isValidInstance) {
            return;
        }
        
        let selectedEntityBox = selectedEntities?.[0]?.getComponent<BoundsComponent>(ComponentId.Bounds)!.boundingBox;
        const highlightPositionToken = highlightEntities?.[0]?.getComponent<TokenPositionComponent>(ComponentId.TokenPosition)!.token;
        if (highlightPositionToken !== undefined && pageTokens) {
            selectedEntityBox = pageTokens[highlightPositionToken];
        }

        const view = viewEntities?.[0]?.getComponent<ViewComponent>(ComponentId.View)!;
        if (!!selectedEntityBox && !!view && !!props.viewRef.current) {
            const leftTopPoint = { x: selectedEntityBox.left, y: selectedEntityBox.top };
            const viewPosition = view.viewport.worldPosition;
            const canvasBounds = props.viewRef.current.getBoundingClientRect();
            const positionViewSpace = worldToViewSpace(leftTopPoint, viewPosition);

            let position = {
                left: positionViewSpace.x + canvasBounds.x,
                top: positionViewSpace.y + canvasBounds.y - 50
            };

            // If the target position is not on any visible page, use the default modal position
            if (!getPageForPos(visiblePages, position.top)) {
                position = defaultModalPosition;
            }

            setPosition(position);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isValidInstance, selectedEntities, highlightEntities, viewEntities, props.viewRef])

    function launchEditLabel() {
        dispatch(setSelectedLabel(tagText.tagText));
    }

    async function removeLabel() {
        if (!instance?.spanId) {
            return;
        }

        await tagText.removeTag();
    }

    const actionsDisabled = tagText.isEditing || !tagText.tagText;
    const removeLabelActionProps: IEditorButtonProps = {
        disabled: actionsDisabled,
        onClick: removeLabel,
        iconProps: {
            iconName: 'RemoveFilter',
            styles: { root: { fontSize: '16px', color: actionsDisabled ? primaryTheme.palette.neutralTertiary : themeRed } }
        },
        tooltipContent: "Remove label from chunk"
    }

    const revertLabelChangesActionProps: IEditorButtonProps = {
        onClick: tagText.undo,
        styles: { root: { opacity: tagText.isEditing ? 1 : 0, cursor: tagText.isEditing ? 'pointer' : 'inherit' } },
        iconProps: {
            iconName: 'Undo',
            styles: { root: { fontSize: '14px' } }
        },
        tooltipContent: "Revert"
    }

    const launchLabelEditorActionProps: IEditorButtonProps = {
        disabled: tagText.isEditing || !tagText.tagText,
        onClick: launchEditLabel,
        iconProps: {
            iconName: 'PageHeaderEdit',
            styles: { root: { fontSize: '18px' } }
        },
        tooltipContent: "Edit label"
    }

    const acceptLabelActionProps: IEditorButtonProps = {
        disabled: !tagText.isValid,
        onClick: tagText.accept,
        iconProps: {
            iconName: 'Accept',
            styles: { root: { fontSize: '16px', color: primaryTheme.palette.themePrimary } }
        },
        tooltipContent: "Save label"
    }

    const getButton = (props: IEditorButtonProps) =>
        <TooltipHost content={props.tooltipContent} delay={TooltipDelay.zero}><IconButton {...props} /></TooltipHost>;

    return (
        <Modal
            isOpen={isValidInstance && !selectedLabel}
            isBlocking={false}
            isDarkOverlay={false}
            isModeless={true}
            isClickableOutsideFocusTrap={true}
            forceFocusInsideTrap={false}
            styles={{
                root: { width: 'unset', height: 'fit-content', left: position.left, top: position.top },
                main: { minHeight: 'unset', minWidth: 'unset', borderRadius: '3px', border: `2px solid ${tagText.isEditing ? primaryTheme.palette.themePrimary : primaryTheme.palette.neutralLighter}` }
            }}
            dragOptions={{
                moveMenuItemText: 'Move',
                closeMenuItemText: 'Close',
                menu: ContextualMenu,
                dragHandleSelector: ".dragTarget"
            }}>
            <DragRoot className='dragTarget'>
                <GrabHandleRoot>
                    <Icon iconName="CalculatorEqualTo" />
                </GrabHandleRoot>
                <InputContainer>
                    <ChunkTagInputField tagText={tagText} small />
                </InputContainer>
                <Divider />
                {getButton(tagText.isEditing ? revertLabelChangesActionProps : removeLabelActionProps)}
                <Divider />
                {getButton(tagText.isEditing ? acceptLabelActionProps : launchLabelEditorActionProps)}
            </DragRoot>
        </Modal>
    )
}